import React, { useState } from 'react';
import {
    Modal,
    ModalBody,
    ModalHeader,
    SearchInput,
    SimpleWizard,
    SimpleWizardStep,
} from '@ygtechnologies/bygr-frontend-component-library';
import LinkProjectSelectProfile from './LinkProjectSelectProfile';
import LinkProjectSelectUsers from './LinkProjectSelectUsers';
import theme from './LinkProjectModal.module.scss';
import { BygrProfile, ProfileMember } from 'types';
import LinkProjectSummary from './LinkProjectSummary';
import { createSubProject } from 'services/projects';

enum Step {
    First,
    Second,
    Third,
}

export interface LinkProjectData {
    parentProjectId: number;
    sellerProfileName: string;
    sellerProfileId: number;
    members: ProfileMember[];
}

interface LinkProjectModalProps {
    isOpen: boolean;
    toggle(): void;
    onClosed?(): void;
    onSelect?(): void;
    onBack?(): void;
    currentProjectId: number;
    onSave(): void;
}

const LinkProjectModal: React.FC<LinkProjectModalProps> = ({
    isOpen,
    toggle,
    onClosed,
    onSelect,
    onBack,
    currentProjectId,
    onSave,
}) => {
    const [currentStep, setCurrentStep] = useState(Step.First);
    const [searchText, setSearchText] = useState('');
    const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
    const [subProjectData, setSubProjectData] = useState<LinkProjectData>({
        sellerProfileId: -1,
        parentProjectId: currentProjectId,
        sellerProfileName: '',
        members: [],
    });
    const steps = [Step.First, Step.Second, Step.Third];

    const handleClose = () => {
        setSelectedProfileId(null);
        setSearchText('');
        setCurrentStep(Step.First);
        onClosed && onClosed();
    };

    const onProfileSelect = (profile: BygrProfile) => {
        setSubProjectData({ ...subProjectData, sellerProfileId: profile.id, sellerProfileName: profile.name });
        setCurrentStep(Step.Second);
        setSelectedProfileId(profile.id);
        setSearchText('');
    };

    const onUsersSelect = (members: ProfileMember[]) => {
        setSubProjectData({ ...subProjectData, members });
        setCurrentStep(Step.Third);
        setSearchText('');
    };

    const handleSave = async () => {
        await createSubProject({
            parentProjectId: subProjectData.parentProjectId,
            sellerProfileId: subProjectData.sellerProfileId,
            members: subProjectData.members.map((member) => member.userId),
        });
        onSave();
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} onClosed={handleClose} size="lg">
            <ModalHeader>Create sub-project</ModalHeader>
            <ModalBody>
                <SimpleWizard
                    onCancel={toggle}
                    onSubmit={toggle}
                    steps={steps}
                    currentStep={currentStep}
                    onStepChange={setCurrentStep}>
                    <SimpleWizardStep header="Select sub-project owner profile" stepNumber={Step.First} hideButtons>
                        <SearchInput
                            className={theme.searchInput}
                            onSearchTextChange={setSearchText}
                            searchText={searchText}
                        />
                        <LinkProjectSelectProfile onSelect={onProfileSelect} searchText={searchText} />
                    </SimpleWizardStep>
                    <SimpleWizardStep
                        header="Select admins for the sub-project"
                        stepNumber={Step.Second}
                        onBack={() => setSearchText('')}
                        hideButtons>
                        <SearchInput
                            className={theme.searchInput}
                            onSearchTextChange={setSearchText}
                            searchText={searchText}
                        />
                        <LinkProjectSelectUsers
                            searchText={searchText}
                            profileId={selectedProfileId}
                            toggle={toggle}
                            onSelect={onUsersSelect}
                            onBack={() => {
                                setCurrentStep(Step.First);
                                setSearchText('');
                                setSubProjectData({
                                    ...subProjectData,
                                    sellerProfileId: -1,
                                    sellerProfileName: '',
                                    members: [],
                                });
                            }}
                        />
                    </SimpleWizardStep>
                    <SimpleWizardStep header="Review" stepNumber={Step.Third} hideButtons>
                        <LinkProjectSummary
                            projectData={subProjectData}
                            onSave={handleSave}
                            onCancel={toggle}
                            onBack={() => setCurrentStep(Step.Second)}
                        />
                    </SimpleWizardStep>
                </SimpleWizard>
            </ModalBody>
        </Modal>
    );
};

export default LinkProjectModal;
